import { useDateTimeDisplay } from '../../../../../platform/util/useDateTimeDisplay';
import CalendarDateTimeField from '../../../../../platform/util/CalendarDateTimeField';
import { zonedTimeToUtc } from 'date-fns-tz';
import React, { useContext, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { UserContext } from "../../../../../platform/components/auth/auth-context";
import { SEND_EMAIL_MUTATION, UPDATE_BOOKING } from "../../../../../platform/graphql/mutations";
import { useCalendar } from "../calendar/calendarContext";
import {
  getBookingActionHtmlBlock,
  getBookingActionLinks,
  getBookingActionTextBlock,
} from "../../../util/bookingEmailActions";

const modalBoxSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  borderRadius: "10px",
  outline: "none",
  width: "min(92vw, 520px)",
};

const NotifyScheduleChange = ({
  currentData,
  showNotifyModal,
  setShowNotifyModal,
  initialChangeType = "general",
}) => {
  const userContext = useContext(UserContext);
  const providerUser = userContext?.providerUser;
  const { displayCalendar, displayClock, dateTimeLabel } = useDateTimeDisplay();
  const timezone = providerUser?.timeZone?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [newTimeValid, setNewTimeValid] = useState(false);
  const { refreshBookings } = useCalendar();

  const [sendEmailMutation] = useMutation(SEND_EMAIL_MUTATION);
  const [updateBookingMutation] = useMutation(UPDATE_BOOKING);
  const [changeType, setChangeType] = useState(initialChangeType === "rescheduled" ? "rescheduled" : "general");
  const [subject, setSubject] = useState("Meeting Update Notification");
  const [newMeetingLink, setNewMeetingLink] = useState("");
  const [newScheduledTime, setNewScheduledTime] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState({ type: "", text: "" });
  const [sending, setSending] = useState(false);
  const [lastSentPayloadKey, setLastSentPayloadKey] = useState("");

  const formatMeetingTime = rawTime => dateTimeLabel(rawTime, timezone);

  const bookings = useMemo(() => {
    const items = Array.isArray(currentData) ? currentData : currentData ? [currentData] : [];
    return items.filter(Boolean);
  }, [currentData]);
  const previewItems = bookings.slice(0, 3).map((booking) => ({
        id: booking?.id,
        title: booking?.bookingTypeName || booking?.quickTitle || "Booking",
        time: formatMeetingTime(booking?.time),
        invitee: String(booking?.inviteeName || "").trim(),
      }));
  const payloadKey = useMemo(() => {
    const ids = bookings.map((b) => Number(b?.id || 0)).filter(Boolean).sort((a, b) => a - b);
    return JSON.stringify({
      ids,
      changeType: String(changeType || "").trim(),
      subject: String(subject || "").trim(),
      link: String(newMeetingLink || "").trim(),
      newScheduledTime: String(newScheduledTime || "").trim(),
      message: String(message || "").trim(),
    });
  }, [bookings, changeType, subject, newMeetingLink, newScheduledTime, message]);
  const canSend =
    !sending &&
    bookings.length > 0 &&
    String(subject || "").trim().length > 0 &&
    (changeType !== "rescheduled" || newTimeValid) &&
    payloadKey !== lastSentPayloadKey;

  const formatRescheduledTime = rawValue => rawValue && newTimeValid ? dateTimeLabel(zonedTimeToUtc(rawValue, timezone), timezone) : "";

  React.useEffect(() => {
    if (changeType === "rescheduled") {
      setSubject("Meeting Rescheduled");
    } else {
      setSubject("Meeting Update Notification");
    }
  }, [changeType]);

  React.useEffect(() => {
    if (!showNotifyModal) return;
    setChangeType(initialChangeType === "rescheduled" ? "rescheduled" : "general");
  }, [initialChangeType, showNotifyModal]);

  const handleClose = () => {
    if (sending) return;
    setShowNotifyModal(false);
    setStatus({ type: "", text: "" });
  };

  const handleSend = async () => {
    if (!bookings.length) {
      setStatus({ type: "error", text: "No booking selected." });
      return;
    }

    const trimmedSubject = String(subject || "").trim();
    if (!trimmedSubject) {
      setStatus({ type: "error", text: "Subject is required." });
      return;
    }
    if (changeType === "rescheduled" && !newTimeValid) {
      setStatus({ type: "error", text: "New date and time are required for a reschedule." });
      return;
    }

    setSending(true);
    setStatus({ type: "", text: "" });
    let sentCount = 0;
    let skippedCount = 0;

    try {
      if (changeType === "rescheduled") {
        const parsedNewTime = zonedTimeToUtc(newScheduledTime, timezone);
        if (Number.isNaN(parsedNewTime.getTime())) {
          setStatus({ type: "error", text: "Invalid new date and time." });
          setSending(false);
          return;
        }

        for (const booking of bookings) {
          if (!booking?.id) continue;
          await updateBookingMutation({
            variables: {
              id: String(booking.id),
              time: parsedNewTime.toISOString(),
            },
          });
        }
        await refreshBookings();
      }

      for (const booking of bookings) {
        const toEmail = String(booking?.inviteeEmail || "").trim();
        const fromEmail = String(booking?.staffEmail || providerUser?.email || "").trim();
        if (!toEmail || !fromEmail) {
          skippedCount += 1;
          continue;
        }

        const meetingTime = formatMeetingTime(booking?.time);
        const meetingTitle = booking?.bookingTypeName || booking?.quickTitle || "Meeting";
        const safeMessage = String(message || "").trim();
        const safeLink = String(newMeetingLink || "").trim();
        const formattedRescheduledTime = formatRescheduledTime(newScheduledTime);
        const bookingActionLinks = getBookingActionLinks({
          origin: window.location.origin,
          bookingId: booking?.id,
          timeValue: changeType === "rescheduled" && newScheduledTime ? zonedTimeToUtc(newScheduledTime, timezone).toISOString() : booking?.time,
          email: toEmail,
        });

        const introLine =
          changeType === "rescheduled"
            ? `Your ${meetingTitle} with ${providerUser?.username || "the provider"} has been rescheduled.`
            : `There is an update for your ${meetingTitle} with ${providerUser?.username || "the provider"}.`;

        const textBody = [
          "Hello,",
          introLine,
          `Current scheduled time: ${meetingTime}`,
          changeType === "rescheduled" && formattedRescheduledTime
            ? `New scheduled time: ${formattedRescheduledTime}`
            : "",
          safeLink ? `Updated meeting link: ${safeLink}` : "",
          safeMessage ? `Update note: ${safeMessage}` : "",
          "",
          getBookingActionTextBlock(bookingActionLinks),
          "Please reply to this email if you have any questions.",
        ]
          .filter(Boolean)
          .join("\n");

        const htmlBody = `
          <p>Hello,</p>
          <p>
            ${introLine}
          </p>
          <p><strong>Current scheduled time:</strong> ${meetingTime}</p>
          ${changeType === "rescheduled" && formattedRescheduledTime ? `<p><strong>New scheduled time:</strong> ${formattedRescheduledTime}</p>` : ""}
          ${safeLink ? `<p><strong>Updated meeting link:</strong> <a href="${safeLink}" target="_blank" rel="noopener noreferrer">${safeLink}</a></p>` : ""}
          ${safeMessage ? `<p><strong>Update note:</strong> ${safeMessage}</p>` : ""}
          ${getBookingActionHtmlBlock({
            ...bookingActionLinks,
            intro: "Need to update this meeting? Use one of the options below.",
          })}
          <p>Please reply to this email if you have any questions.</p>
        `;

        await sendEmailMutation({
          variables: {
            bookingId: String(booking.id),
        from_email: fromEmail,
            to: toEmail,
            subject: trimmedSubject,
            text: textBody,
            html: htmlBody,
          },
        });
        sentCount += 1;
      }

      if (!sentCount) {
        setStatus({
          type: "error",
          text: "No notification was sent. Invitee emails were missing.",
        });
      } else {
        setLastSentPayloadKey(payloadKey);
        setStatus({
          type: "success",
          text:
            skippedCount > 0
              ? `Sent ${sentCount} update notification(s). Skipped ${skippedCount} booking(s) without email.`
              : `Sent ${sentCount} update notification(s).`,
        });
      }
    } catch (error) {
      setStatus({
        type: "error",
        text: error?.message || "Failed to send update notification.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={showNotifyModal} onClose={handleClose}>
      <Box sx={modalBoxSx}>
        <Typography variant="h6" mb={1}>
          Send Change Notification
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Notify {bookings.length} selected booking{bookings.length === 1 ? "" : "s"} about updates.
        </Typography>
        {previewItems.length > 0 ? (
          <Box
            sx={{
              mb: 2,
              px: 1.25,
              py: 1,
              borderRadius: "8px",
              backgroundColor: "rgba(15,23,42,0.04)",
              border: "1px solid rgba(15,23,42,0.08)",
            }}
          >
            {previewItems.map((item) => (
              <Typography key={item.id || `${item.title}-${item.time}`} variant="body2" sx={{ mb: 0.5 }}>
                <strong>{item.title}</strong>
                {item.time ? ` - ${item.time}` : ""}
                {item.invitee ? ` - ${item.invitee}` : ""}
              </Typography>
            ))}
            {bookings.length > previewItems.length ? (
              <Typography variant="caption" color="text.secondary">
                +{bookings.length - previewItems.length} more booking(s)
              </Typography>
            ) : null}
          </Box>
        ) : null}

        <Stack spacing={1.25}>
          <FormControl size="small">
            <InputLabel id="change-type-label">Update type</InputLabel>
            <Select
              labelId="change-type-label"
              label="Update type"
              value={changeType}
              onChange={(e) => setChangeType(e.target.value)}
            >
              <MenuItem value="general">General update</MenuItem>
              <MenuItem value="rescheduled">Rescheduled</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          {changeType === "rescheduled" ? (
            <CalendarDateTimeField label="New appointment" value={newScheduledTime}
              onChange={setNewScheduledTime} calendar={displayCalendar} clock={displayClock}
              timezone={timezone} onValidityChange={setNewTimeValid} />
          ) : null}
          <TextField
            size="small"
            label="New meeting link (optional)"
            placeholder="https://meet.google.com/..."
            value={newMeetingLink}
            onChange={(e) => setNewMeetingLink(e.target.value)}
          />
          <TextField
            size="small"
            label="Update note (optional)"
            multiline
            minRows={4}
            placeholder={
              changeType === "rescheduled"
                ? "Add a short note about why the time changed."
                : "Add any extra details the invitee should know."
            }
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          {status.text ? <Alert severity={status.type || "info"}>{status.text}</Alert> : null}
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 2 }}>
          <Button variant="outlined" onClick={handleClose} disabled={sending}>
            Close
          </Button>
          <Button variant="contained" onClick={handleSend} disabled={!canSend}>
            {sending ? "Sending..." : "Send Update"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default NotifyScheduleChange;
